import { type FC, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { motion } from 'framer-motion';

import {
  useBuyScratchTicketMutation,
  useRevealScratchTicketMutation,
} from '@/api/queries/scratch';
import { useStorePrice } from '@/common/hooks/apple-price';
import { Playdeck } from '@/common/lib/playdeck';
import {
  type ScratchCell,
  type ScratchOffer,
  type ScratchRewardType,
  type ScratchTicket,
} from '@/common/types/scratch';
import { AdsContext } from '@/components/Ads/context';
import { ModalContext } from '@/components/Modals/Modal/config';
import { WatchButton } from '@/components/ui/Button/Button';
import { CurrencyImage } from '@/components/ui/CurrencyImage/CurrencyImage';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import {
  applyBoughtTicket,
  applyRevealedTicket,
  setProgress,
} from '@/redux/reducers/scratchReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { ScratchSurface } from '../ScratchSurface/ScratchSurface';

import css from './styles.module.scss';

const STATIC_IMG: Partial<Record<ScratchRewardType, string>> = {
  goldTicket: '/img/icons/ticket-gold.webp',
  silverTicket: '/img/icons/ticket-silver.webp',
  diamondTicket: '/img/icons/ticket-diamond.webp',
  starPoints: '/img/icons/star-large.png',
  bingoBalls: '/img/bingo/bingo-icon.png',
};

const CURRENCY_MAP: Partial<
  Record<ScratchRewardType, 'tgem' | 'cash' | 'dice' | 'star'>
> = {
  tgem: 'tgem',
  cash: 'cash',
  dice: 'dice',
};

interface CellProps {
  cell: ScratchCell;
  /** Whether this cell's reward matches the player's selected target. */
  matchesTarget: boolean;
  /** Show the cell content (foil scratched off / ticket revealed). */
  visible: boolean;
}

const ScratchCellView: FC<CellProps> = ({ cell, matchesTarget, visible }) => {
  const { symbol } = cell;
  const currencyKind = CURRENCY_MAP[symbol.rewardType];
  const imgSrc = !currencyKind ? (STATIC_IMG[symbol.rewardType] ?? null) : null;

  return (
    <div
      className={cn(
        css.cell,
        css[`rarity_${matchesTarget ? symbol.rarity : 'none'}`],
        visible && matchesTarget && css.cellWinning,
      )}
    >
      <div className={css.cellInner}>
        {/* Non-matching cells render the same "zero" art used in the dice
         * game — keeps the player from misreading filler symbols as
         * "almost won" when only a triple of the chosen reward pays out. */}
        {!matchesTarget ? (
          <>
            <img
              src="/img/dice-game/zero.png"
              alt=""
              className={css.cellIcon}
              draggable={false}
            />
            <span className={css.cellValue}>×0</span>
          </>
        ) : currencyKind ? (
          <>
            <CurrencyImage kind={currencyKind} size="38px" />
            <span className={css.cellValue}>×{symbol.value}</span>
          </>
        ) : imgSrc ? (
          <>
            <img src={imgSrc} alt="" className={css.cellIcon} />
            <span className={css.cellValue}>×{symbol.value}</span>
          </>
        ) : (
          <span className={css.cellValue}>×{symbol.value}</span>
        )}
      </div>
    </div>
  );
};

const PrizePreview: FC<{ rewardType: ScratchRewardType; value: number }> = ({
  rewardType,
  value,
}) => {
  const currencyKind = CURRENCY_MAP[rewardType];
  const imgSrc = !currencyKind ? (STATIC_IMG[rewardType] ?? null) : null;

  return (
    <div className={css.pickerPreview}>
      {currencyKind ? (
        <CurrencyImage kind={currencyKind} size="34px" />
      ) : imgSrc ? (
        <img src={imgSrc} alt="" className={css.pickerPreviewImg} />
      ) : null}
      <span className={css.pickerPreviewValue}>×{value}</span>
    </div>
  );
};

interface ScratchOfferCardProps {
  offer: ScratchOffer;
  /** Page-level picker control — page owns the picker so its fixed positioning
   * is not affected by the card's framer-motion transforms. */
  onOpenPicker: () => void;
}

/**
 * Self-contained offer card. Holds the scratch surface — when the player
 * buys, the placeholder hexes are replaced by scratchable prize cells
 * inside this same card. The reward picker lives at the page level.
 */
const ScratchOfferCard: FC<ScratchOfferCardProps> = ({
  offer,
  onOpenPicker,
}) => {
  const storePrice = useStorePrice(offer.amountUsd ?? undefined);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const active = useAppSelector((s) => s.scratch.active);
  const [buyTicketMutation, buyTicketState] = useBuyScratchTicketMutation();
  const [revealTicketMutation] = useRevealScratchTicketMutation();
  const { openModal } = useContext(ModalContext);
  const { showAd, adsIsLoading } = useContext(AdsContext);
  const isVip = useAppSelector((s) => s.user.user?.VIP ?? false);

  // Find this offer's in-progress ticket (≤1 per offerId).
  const ticket: ScratchTicket | null =
    active.find((t) => t.offerId === offer.id) ?? null;
  const isThisOfferActive = ticket !== null;
  const revealed =
    ticket?.status === 'revealed' || ticket?.status === 'claimed';

  const soldOut = offer.purchasesLeft <= 0;
  const noReward = !offer.selectedRewardKey;
  // Player can buy from another offer even with an active ticket on a
  // different offer — backend still enforces ACTIVE_TICKET_EXISTS so a
  // server reject will surface as a toast. Hard blockers (sold-out /
  // same-offer in-progress ticket) still disable here.
  const buyDisabled = soldOut || isThisOfferActive;

  // The selection shown on the picker cell prefers the in-progress
  // ticket's reward (server-canonical, survives page reloads) over the
  // ephemeral pre-buy offer.selectedRewardKey (client-only, lost on
  // refresh). This way the player still sees what they're scratching for
  // after a hard reload.
  const effectiveRewardKey =
    ticket?.selectedRewardKey ?? offer.selectedRewardKey ?? null;
  const selectedPrize = effectiveRewardKey
    ? (offer.rewardPool.find(
        (p) => `${p.rewardType}-${p.value}` === effectiveRewardKey,
      ) ?? null)
    : null;

  const handleProgress = useCallback(
    (p: number): void => {
      dispatch(setProgress({ offerId: offer.id, progress: p }));
    },
    [dispatch, offer.id],
  );

  const handleReveal = useCallback((): void => {
    if (!ticket) return;
    const ticketId = ticket.id;
    window.setTimeout(() => {
      void revealTicketMutation({ ticketId })
        .unwrap()
        .then((res) => {
          dispatch(applyRevealedTicket(res.ticket));
          // Reveal analytics — match count drives the multiplier funnel.
          Playdeck.sendAnalitics({
            type: 'custom_event',
            name: 'scratch_ticket_revealed',
            user_properties: {},
            event_properties: {
              ticket_id: res.ticket.id,
              offer_id: res.ticket.offerId,
              match_count: res.ticket.matchCount,
              prize_type: res.ticket.prize?.rewardType ?? null,
              prize_value: res.ticket.prize?.value ?? null,
              selected_reward_key: res.ticket.selectedRewardKey,
            },
          });
        })
        .catch((err) => {
          const msg =
            (err as { data?: { message?: string } })?.data?.message ?? '';

          // Idempotent endpoint — if it 409s on already-claimed, ignore.
          if (msg && msg !== 'TICKET_GONE') {
            showToast(msg);
          }
        });
    }, 380);
  }, [dispatch, revealTicketMutation, ticket]);

  const buyDisabledByLoading = buyTicketState.isLoading;

  const handleBuy = useCallback((): void => {
    // Hard guard — never send a buy request when the offer is sold out
    // for the current event window. Saves a round-trip and skips the
    // 422 OFFER_SOLD_OUT toast.
    if (offer.purchasesLeft <= 0) {
      showToast(t('No purchases left for this offer'));

      return;
    }

    if (noReward || !offer.selectedRewardKey) {
      showToast(t('Choose a reward before purchase'));

      return;
    }

    // Track that a purchase flow has started — modal opens or the ads
    // mutation fires immediately after this point.
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'scratch_purchase_started',
      user_properties: {},
      event_properties: {
        offer_id: offer.id,
        offer_paid: offer.paid,
        reward_key: offer.selectedRewardKey,
        amount_usd: offer.amountUsd ?? 0,
        amount_stars: offer.amountStars ?? 0,
      },
    });

    // Paid offers (basic / standard / premium) → open the standard
    // currency-selection modal. The modal owns the actual mutation
    // call + payment status flow.
    if (offer.paid && offer.id !== 'ads') {
      openModal({
        renderKind: 'buyScratchTicketModal',
        props: {
          offerId: offer.id,
          rewardKey: offer.selectedRewardKey,
        },
      });

      return;
    }

    // Free `ads` flow — VIP users skip the ad entirely, regular users
    // must watch one before we hit the buy mutation.
    const fireBuy = (): void => {
      if (!offer.selectedRewardKey) return;
      void buyTicketMutation({
        offerId: offer.id,
        rewardKey: offer.selectedRewardKey,
      })
        .unwrap()
        .then((res) => {
          if (res.ticket) dispatch(applyBoughtTicket(res.ticket));
        })
        .catch((err) => {
          const msg =
            (err as { data?: { message?: string } })?.data?.message ??
            'Something went wrong';
          showToast(msg);
        });
    };

    if (isVip) {
      fireBuy();

      return;
    }

    showAd({
      placements: 'scratch',
      onReward: fireBuy,
      onError: () => {
        showToast(t('Ads launch error'));
      },
    });
  }, [
    buyTicketMutation,
    dispatch,
    isVip,
    noReward,
    offer,
    openModal,
    showAd,
    t,
  ]);

  return (
    <motion.div
      className={cn(
        css.card,
        css[`frame_${offer.frame}`],
        !isThisOfferActive && !buyDisabled && css.cardClickable,
        soldOut && css.cardSoldOut,
      )}
      whileTap={buyDisabled && !isThisOfferActive ? undefined : { scale: 0.99 }}
      role={!isThisOfferActive && !buyDisabled ? 'button' : undefined}
      tabIndex={!isThisOfferActive && !buyDisabled ? 0 : undefined}
      onClick={!isThisOfferActive && !buyDisabled ? handleBuy : undefined}
    >
      <div className={css.parchment}>
        {/* Left "+ pick reward" cell */}
        <button
          type="button"
          className={css.pickerCell}
          onClick={(e) => {
            e.stopPropagation();
            onOpenPicker();
          }}
          aria-label={selectedPrize ? t('Change reward') : t('Choose reward')}
          disabled={isThisOfferActive && !revealed}
        >
          {selectedPrize ? (
            <>
              <PrizePreview
                rewardType={selectedPrize.rewardType}
                value={selectedPrize.value}
              />
              {/* Swap-arrows badge — visual hint that the reward is editable
               * via tapping the cell again. Whole cell is the click target. */}
              <span className={css.swapBadge} aria-hidden>
                <svg viewBox="0 0 16 16" width="11" height="11">
                  <path
                    d="M3 6h8l-2-2M13 10H5l2 2"
                    stroke="#fff"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </span>
            </>
          ) : (
            <>
              <span className={css.pickerPlus}>+</span>
              <span className={css.pickerLabel}>{t('Choose reward')}</span>
            </>
          )}
        </button>

        {/* Right area: 3 cells (when active) or 3 placeholder hexes */}
        {ticket ? (
          <div className={css.cellsRow}>
            {ticket.cells.map((cell) => {
              const cellKey = `${cell.symbol.rewardType}-${cell.symbol.value}`;
              const matchesTarget = cellKey === ticket.selectedRewardKey;

              return (
                <ScratchCellView
                  key={cell.index}
                  cell={cell}
                  matchesTarget={matchesTarget}
                  visible={revealed}
                />
              );
            })}
            <ScratchSurface
              ticketId={ticket.id}
              disabled={revealed}
              onProgress={handleProgress}
              onReveal={handleReveal}
            />
          </div>
        ) : (
          <div className={css.hexes}>
            {[0, 1, 2].map((i) => (
              <div className={css.hex} key={i}>
                <img
                  src="/img/airdrop/Star.png"
                  alt=""
                  className={css.hexLogo}
                  draggable={false}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={css.statusBar}>
        <span className={css.statusLeft}>
          {t('Remaining')}: {offer.purchasesLeft}
        </span>

        {isThisOfferActive && !revealed ? (
          <span className={css.statusAction}>{t('Scratching')}</span>
        ) : !offer.paid ? (
          // Free offer — reuses the standard ADS / VIP-Free button so the
          // VIP variant looks identical to the boost flows.
          <WatchButton
            isVIP={isVip}
            isLoading={buyDisabledByLoading || adsIsLoading}
            disabled={
              buyDisabled ||
              buyDisabledByLoading ||
              (isVip ? false : adsIsLoading)
            }
            className={css.watchBtn}
            onClick={(e) => {
              e.stopPropagation();
              handleBuy();
            }}
          />
        ) : (
          <button
            type="button"
            className={cn(
              css.priceBtn,
              soldOut && css.priceBtnSoldOut,
              noReward && css.priceBtnNeedsReward,
            )}
            disabled={buyDisabled || buyDisabledByLoading}
            onClick={(e) => {
              e.stopPropagation();
              handleBuy();
            }}
          >
            <span className={css.priceText}>
              {offer.amountUsd != null
                ? (storePrice ?? `$${offer.amountUsd.toFixed(2)}`)
                : `★${offer.amountStars ?? 0}`}
            </span>
          </button>
        )}
      </div>
    </motion.div>
  );
};

export { ScratchOfferCard };
